/**
 * CollaboratorsList Component
 *
 * Displays collaborators in a shared DD with their roles and conclusions.
 */

import React from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { User, Crown, UserCheck, Eye, X, Clock } from 'lucide-react-native';
import Animated, { FadeIn, Layout } from 'react-native-reanimated';

import { useThemeColors } from '@/lib/useThemeColors';
import type { DDCollaborator, CollaboratorRole } from '@/lib/shared-dd';
import { ROLE_CONFIG, CONCLUSION_CONFIG } from '@/lib/shared-dd';
import AnimatedPressable from '@/components/AnimatedPressable';

// ============================================================================
// Types
// ============================================================================

interface CollaboratorsListProps {
  collaborators: DDCollaborator[];
  currentUserId: string;
  isOwner: boolean;
  syndicateMode: boolean;
  onRemove?: (collaboratorId: string) => void;
}

// ============================================================================
// Component
// ============================================================================

export function CollaboratorsList({
  collaborators,
  currentUserId,
  isOwner,
  syndicateMode,
  onRemove,
}: CollaboratorsListProps) {
  const colors = useThemeColors();

  const activeCollaborators = collaborators.filter(
    (c) => c.status === 'active' || c.status === 'pending'
  );

  const getRoleIcon = (role: CollaboratorRole) => {
    switch (role) {
      case 'owner':
        return <Crown size={14} color={colors.warning} />;
      case 'collaborator':
        return <UserCheck size={14} color={colors.primary[500]} />;
      case 'viewer':
        return <Eye size={14} color={colors.text.tertiary} />;
    }
  };

  return (
    <View style={styles.container}>
      <Text style={[styles.title, { color: colors.text.primary }]}>
        Collaborators ({activeCollaborators.length})
      </Text>

      <View style={styles.list}>
        {activeCollaborators.map((collaborator, index) => (
          <Animated.View
            key={collaborator.id}
            entering={FadeIn.delay(index * 50).duration(200)}
            layout={Layout.springify()}
          >
            <View
              style={[
                styles.item,
                {
                  backgroundColor: colors.surface.secondary,
                  borderColor:
                    collaborator.status === 'pending'
                      ? colors.border.light
                      : 'transparent',
                },
              ]}
            >
              {/* Avatar */}
              <View
                style={[
                  styles.avatar,
                  {
                    backgroundColor:
                      collaborator.role === 'owner'
                        ? colors.traffic.amberLight
                        : colors.primary[50],
                  },
                ]}
              >
                {collaborator.status === 'pending' ? (
                  <Clock size={16} color={colors.text.tertiary} />
                ) : (
                  <User
                    size={16}
                    color={
                      collaborator.role === 'owner'
                        ? colors.warning
                        : colors.primary[500]
                    }
                  />
                )}
              </View>

              {/* Info */}
              <View style={styles.info}>
                <View style={styles.nameRow}>
                  <Text
                    style={[
                      styles.name,
                      {
                        color:
                          collaborator.status === 'pending'
                            ? colors.text.tertiary
                            : colors.text.primary,
                      },
                    ]}
                    numberOfLines={1}
                  >
                    {collaborator.investorName}
                    {collaborator.investorId === currentUserId && ' (you)'}
                  </Text>
                  {getRoleIcon(collaborator.role)}
                </View>

                {/* Status / Conclusion */}
                {collaborator.status === 'pending' ? (
                  <Text style={[styles.status, { color: colors.text.tertiary }]}>
                    Invite pending
                  </Text>
                ) : syndicateMode && collaborator.conclusion ? (
                  <View style={styles.conclusionRow}>
                    <Text style={styles.conclusionEmoji}>
                      {CONCLUSION_CONFIG[collaborator.conclusion.decision].emoji}
                    </Text>
                    <Text
                      style={[
                        styles.conclusionText,
                        { color: colors.text.secondary },
                      ]}
                    >
                      {CONCLUSION_CONFIG[collaborator.conclusion.decision].label}
                      {collaborator.conclusion.investmentAmount && (
                        <Text style={{ color: colors.success }}>
                          {' '}
                          • CHF {collaborator.conclusion.investmentAmount.toLocaleString()}
                        </Text>
                      )}
                    </Text>
                  </View>
                ) : (
                  <Text style={[styles.status, { color: colors.text.tertiary }]}>
                    {ROLE_CONFIG[collaborator.role].label}
                  </Text>
                )}
              </View>

              {/* Remove button (owner only, can't remove self) */}
              {isOwner &&
                collaborator.investorId !== currentUserId &&
                onRemove && (
                  <Pressable
                    onPress={() => onRemove(collaborator.id)}
                    style={({ pressed }) => [
                      styles.removeButton,
                      {
                        backgroundColor: pressed
                          ? colors.traffic.redLight
                          : 'transparent',
                      },
                    ]}
                  >
                    <X size={16} color={colors.error} />
                  </Pressable>
                )}
            </View>
          </Animated.View>
        ))}
      </View>
    </View>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles = StyleSheet.create({
  container: {
    gap: 12,
  },
  title: {
    fontSize: 14,
    fontWeight: '600',
  },
  list: {
    gap: 8,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
    borderRadius: 12,
    borderWidth: 1,
    gap: 10,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  info: {
    flex: 1,
    gap: 2,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  name: {
    fontSize: 14,
    fontWeight: '500',
  },
  status: {
    fontSize: 12,
  },
  conclusionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  conclusionEmoji: {
    fontSize: 12,
  },
  conclusionText: {
    fontSize: 12,
  },
  removeButton: {
    width: 28,
    height: 28,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
